/**
 * Science project covers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_project_pages`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const scienceProjectCoversPack: ToilePack = {
    slug: 'science-project-covers',
    category: 'classroom',
    name: 'Science project covers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'atom', label: 'Atom', subject: 'a cover with an atom, a flask and a microscope doodled around "SCIENCE PROJECT"'},
        {key: 'plants', label: 'Plants', subject: 'a cover with leaves and a plant cell around a title box'},
        {key: 'space', label: 'Space', subject: 'a cover with planets and a rocket'},
    ],
    styles: [
        {key: 'sketch', label: 'Sketch pens', style: 'outlined in black fineliner and filled in with bright sketch pens in flat even colour, neat hand-drawn lines with a slight wobble', palette: 'bright blues, greens, oranges and purples', framing: 'centred on a white A4 sheet seen straight on with an even clean margin all round'},
        {key: 'pencil', label: 'Colour pencils', style: 'drawn in sharp colour pencils with soft layered shading and visible pencil strokes, a gentle hand-coloured finish', palette: 'soft pencil tones of leaf green, sky blue and violet', framing: 'filling a white A4 sheet seen straight on, a narrow pencil-ruled margin line inside the edge of the page'},
        {key: 'duo', label: 'Two-colour pen', style: 'drawn in black gel pen with one accent colour of highlighter, clean ink lines, minimal and tidy', palette: 'black ink with green highlighter only', framing: 'on a cream notebook page seen straight on with a generous margin'},
    ],
    directions: ['human body', 'volcano', 'robot'],
    directionHint: 'try: human body',
    seo: {
        title: 'Science project covers: atoms, plants, space',
        metaDescription: 'Nine hand-drawn science project covers: atoms, plants and space designs, in sketch pens, colour pencils or gel pen.',
        h1: 'Science project covers: atoms, plants, space',
        tagline: 'Nine covers: atoms, plants and space, each in three pen styles.',
        intro: 'Front pages for a science project file, drawn by hand with pens and colours. The atom cover has an atom, a flask and a microscope drawn around the words SCIENCE PROJECT. The plants cover has leaves and a plant cell around a title box. The space cover has planets and a rocket. Each comes in three pen styles. Type a twist, such as human body, volcano or robot, and the same three subjects can take it.',
        useCases: [
            'a science fair project',
            'a class science project file',
            'a lab practical file',
        ],
        faq: [
            {q: 'What is on the atom cover?', a: 'An atom, a flask and a microscope, drawn around the words SCIENCE PROJECT.'},
            {q: 'What is on the plants and space covers?', a: 'The plants cover has leaves and a plant cell around a title box. The space cover has planets and a rocket.'},
            {q: 'What are the three styles?', a: 'Sketch pens use bright flat colour inside black fineliner outlines. Colour pencils give soft layered shading in pastel tones. Two-colour pen is black gel ink with one highlighter colour.'},
            {q: 'Can I ask for a human body or volcano cover?', a: 'Yes. Type it as a twist and it is a request for the same layouts, not a promised result.'},
        ],
    },
}
